import React from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container
} from '@mui/material';
import { Menu, Close } from '@mui/icons-material';
import Logo from '../../shared/Logo';
import LanguageSwitcher from '../../shared/LanguageSwitcher';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

const Navigation = ({ onGetStarted, mobileMenuOpen, setMobileMenuOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useLanguage();

  const navItems = [
    { label: t('navigation.features'), href: '#features' },
    //{ label: t('navigation.howItWorks'), href: '#how-it-works' },
    { label: t('navigation.templates'), href: '#templates' },
    { label: t('navigation.pricing'), href: '#pricing' },
    { label: t('navigation.faq'), href: '#faq' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={1}
        sx={{
          backgroundColor: tokens.colors.alpha.black[800],
          backdropFilter: 'blur(8px)',
          boxShadow: tokens.shadows.sm,
          borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
          color: tokens.colors.text.primary
        }}
      >
        <Container maxWidth="lg">
          <Toolbar 
            sx={{ 
              justifyContent: tokens.components.header.layout.justifyContent,
              alignItems: tokens.components.header.layout.alignItems,
              padding: isMobile ? tokens.components.header.padding.mobile : tokens.components.header.padding.desktop,
              minHeight: tokens.components.header.layout.minHeight,
              maxHeight: tokens.components.header.layout.maxHeight,
              height: tokens.components.header.height,
            }}
          >
            {/* Logo */}
            <Logo 
              size="medium"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{ 
                px: isMobile ? tokens.components.header.logo.padding.mobile : tokens.components.header.logo.padding.desktop,
                flexShrink: tokens.components.header.logo.flexShrink
              }}
            />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: tokens.components.header.navigation.gap.desktop,
                flexShrink: 1,
                overflow: 'hidden',
                px: isMobile ? tokens.components.header.logo.padding.mobile : tokens.components.header.logo.padding.desktop,
              }}>
                {navItems.map((item, index) => (
                  <Button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    sx={{
                      color: tokens.colors.text.secondary,
                      fontWeight: tokens.typography.fontWeights.medium,
                      textTransform: 'none',
                      fontSize: tokens.components.header.buttons.fontSize,
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      px: tokens.components.header.navigation.padding.desktop.split(' ')[1],
                      py: tokens.components.header.navigation.padding.desktop.split(' ')[0],
                      // Add extra left padding to the first navigation item (Features)
                      ...(index === 0 && {
                        pl: `calc(${tokens.components.header.navigation.padding.desktop.split(' ')[1]} * 2)`
                      }),
                      borderRadius: tokens.radius.base,
                      '&:hover': {
                        color: tokens.colors.primary.main,
                        backgroundColor: tokens.colors.primary.main + '10'
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <LanguageSwitcher 
                  variant="compact" 
                  size="small" 
                  showLabel={false}
                  position="right"
                />
                <Button
                  variant="contained"
                  onClick={onGetStarted}
                  sx={{
                    backgroundColor: tokens.colors.primary.main,
                    color: 'white',
                    fontWeight: tokens.typography.fontWeights.semibold,
                    textTransform: 'none',
                    px: 2, // Reduced padding for more compact button
                    py: isMobile ? tokens.components.header.buttons.padding.mobile.split(' ')[0] : tokens.components.header.buttons.padding.desktop.split(' ')[0],
                    fontSize: tokens.components.header.buttons.fontSize,
                    borderRadius: tokens.radius.base,
                    boxShadow: tokens.shadows.md,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    minWidth: 'auto', // Remove fixed minWidth to allow natural sizing
                    ml: 1,
                    '&:hover': {
                      backgroundColor: tokens.colors.primary.dark,
                      boxShadow: tokens.shadows.lg
                    }
                  }}
                >
                  {t('navigation.getStartedFree')}
                </Button>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ 
                  color: tokens.colors.text.primary,
                  p: 1,
                  flexShrink: 0
                }}
              >
                <Menu />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 320 },
            backgroundColor: 'white',
            pt: 2
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid ${tokens.colors.borders.light}`
        }}>
          <Logo size="medium" />
          <IconButton 
            onClick={() => setMobileMenuOpen(false)}
            sx={{ color: tokens.colors.text.primary }}
          >
            <Close />
          </IconButton>
        </Box>
        
        <List sx={{ pt: 0 }}>
          {navItems.map((item) => (
            <ListItem 
              key={item.label}
              onClick={() => scrollToSection(item.href)}
              sx={{ 
                cursor: 'pointer',
                py: 2,
                px: 3,
                '&:hover': {
                  backgroundColor: tokens.colors.alpha.black[50]
                }
              }}
            >
              <ListItemText 
                primary={item.label}
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: tokens.typography.fontWeights.medium,
                    color: tokens.colors.text.primary,
                    fontSize: '1.1rem'
                  }
                }}
              />
            </ListItem>
          ))}
          <ListItem sx={{ pt: 2, px: 3, justifyContent: 'center' }}>
            <LanguageSwitcher 
              variant="compact" 
              size="medium" 
              showLabel={true}
              position="center"
            />
          </ListItem>
          <ListItem sx={{ pt: 1, px: 3 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={onGetStarted}
              sx={{
                backgroundColor: tokens.colors.primary.main,
                color: 'white',
                fontWeight: tokens.typography.fontWeights.semibold,
                textTransform: 'none',
                py: 1.5,
                borderRadius: tokens.radius.base,
                fontSize: '1.1rem',
                '&:hover': {
                  backgroundColor: tokens.colors.primary.dark
                }
              }}
            >
              {t('navigation.getStartedFree')}
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Navigation; 